import {useEffect, useMemo} from 'react'
import i18n from 'i18next'
import {useSelector} from 'react-redux'
import actions from 'redux-auto'
import {getAusbildungsplan} from '#services/storage'
import {useAusbildungsplanIds} from '#hooks/useAusbildungsplanIds'

const findParent = full => currentItem => {
  for (let i = 0; i < full.length; i += 1) {
    const sub = full[i]
    for (let j = 0; j < sub.content.length; j += 1) {
      if (sub && sub.content[j].id === currentItem) {
        return sub
      }
    }
  }
}

const findDeep = full => currentItem => {
  for (let i = 0; i < full.length; i += 1) {
    const sub = full[i]
    if (sub) {
      if (sub.id === currentItem) {
        return sub
      }
      for (let j = 0; j < sub.content.length; j += 1) {
        if (sub.content[j].id === currentItem) {
          return sub.content[j]
        }
      }
    }
  }
}

const findChildren = full => currentItem => {
  for (let i = 0; i < full.length; i += 1) {
    const sub = full[i]
    if (sub && sub.id === currentItem) {
      return sub.content
    }
  }
}

const getTitleForChapter = full => chapterId => {
  if (findDeep(full)(chapterId)) {
    return findDeep(full)(chapterId).text
  }
  return i18n.t('unknown')
}

// eslint-disable-next-line max-len
const ausbildungsplanIsLoadingSelector = () => state => state.ausbildungsplan?.loading?.getAusbildungsplan

const useAusbildungsplanJSON = ausbildungsplanId => {
  const ausbildungsplanIsLoading = useSelector(ausbildungsplanIsLoadingSelector())
  const ausbildungsplan = getAusbildungsplan(ausbildungsplanId)
  useEffect(() => {
    if (ausbildungsplanId && !ausbildungsplanIsLoading && !ausbildungsplan) {
      actions.ausbildungsplan.getAusbildungsplan({ausbildungsplanId})
    }
  }, [ausbildungsplanId])

  if (ausbildungsplan) {
    const staticPart = ausbildungsplan.static || []
    return {
      ready: true,
      static: staticPart,
      fragen: ausbildungsplan.fragen,
      full: staticPart.concat(ausbildungsplan.fragen),
    }
  }

  return {
    static: [],
    fragen: [],
    full: [],
    ready: false,
  }
}

export function useAusbildungsplan(userId) {
  const ausbildungsplanIds = useAusbildungsplanIds(userId)
  const ausbildungsplan = useAusbildungsplanJSON(ausbildungsplanIds.ausbildungsplanId)
  const aufbauAusbildungsplan = useAusbildungsplanJSON(ausbildungsplanIds.aufbauAusbildungsplanId)

  const mergedPlans = ausbildungsplanIds.aufbauAusbildungsplanId
    ? mergeAufbauAusbildungsplan(ausbildungsplan, aufbauAusbildungsplan)
    : ausbildungsplan

  return useMemo(
    () => ({
      ...mergedPlans,
      getStaticContent: () => mergedPlans.static,
      getQuestionsContent: () => mergedPlans.fragen,
      getFullContent: () => mergedPlans.full,
      findParent: findParent(mergedPlans.full),
      findDeep: findDeep(mergedPlans.full),
      findChildren: findChildren(mergedPlans.full),
      getTitleForChapter: getTitleForChapter(mergedPlans.full),
    }),
    [ausbildungsplan, aufbauAusbildungsplan],
  )
}

const mergeAufbauAusbildungsplan = (ausbildungsplan, aufbauAusbildungsplan) => ({
  ready: ausbildungsplan.ready && aufbauAusbildungsplan.ready,
  static: ausbildungsplan.static,
  fragen: ausbildungsplan.fragen.concat(aufbauAusbildungsplan.fragen),
  full: ausbildungsplan.full.concat(aufbauAusbildungsplan.fragen),
})
